import { Box, Button, Typography } from '@mui/material'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import useStyles, { sketcherSx } from './_styles'
import { TransmissionSketcherProps } from './_props'
import TransmissionSketcherKonva from './TransmissionSketcherKonva'
import type { KonvaStroke, KonvaToolType } from './_props'
import CloseIcon from '@mui/icons-material/Close'
import Divider from '@mui/material/Divider'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import LockIcon from '@mui/icons-material/Lock'
import CommonTooltip from 'libs/frontend-kit/src/lib/components/common/tooltip/Tooltip'
import type Konva from 'konva'
import { useParams } from 'react-router'
import {
    getIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJMediaId,
    useDeleteIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ,
    usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ,
} from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { BodyPartFemaleUrl, BodyPartMaleUrl } from '../../../assets/images/image'

function makeId() {
    return Math.random().toString(16).slice(2) + '-' + Date.now().toString(16)
}

const parseStrokes = (value?: string | null): KonvaStroke[] => {
    if (!value) return []
    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const IMAGE_URL_REGEX = /^https?:\/\//
const EXPORT_PIXEL_RATIO = 2
const UPLOAD_DEBOUNCE_MS = 800

// the answer is: strokes when drawn on ids, or the url of the flattened picture when drawn on caregiver
// strokes always win
const splitInitialValue = (initialValue?: string | string[] | null) => {
    const values = (Array.isArray(initialValue) ? initialValue : [initialValue]).filter(
        (value): value is string => typeof value === 'string' && value.length > 0,
    )
    return {
        strokesValue: values.find((value) => !IMAGE_URL_REGEX.test(value)) ?? '',
        imageUrl: values.find((value) => IMAGE_URL_REGEX.test(value)),
    }
}

const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = src
    })

// flattens the body picture and the strokes so caregiver can show the drawing
// strokes are captured first and synchronously: the stage is destroyed when the question is left
const captureSketch = (stage: Konva.Stage) => ({
    size: stage.size(),
    strokesCanvas: stage.toCanvas({ pixelRatio: EXPORT_PIXEL_RATIO }),
})

const exportSketchBlob = async (
    { size, strokesCanvas }: ReturnType<typeof captureSketch>,
    bodyUrl: string,
): Promise<Blob | null> => {
    const body = await loadImage(bodyUrl)
    const canvas = document.createElement('canvas')
    canvas.width = size.width * EXPORT_PIXEL_RATIO
    canvas.height = size.height * EXPORT_PIXEL_RATIO
    const context = canvas.getContext('2d')
    if (!context) return null

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    const scale = Math.max(canvas.width / body.naturalWidth, canvas.height / body.naturalHeight)
    const drawWidth = body.naturalWidth * scale
    const drawHeight = body.naturalHeight * scale
    context.drawImage(body, (canvas.width - drawWidth) / 2, (canvas.height - drawHeight) / 2, drawWidth, drawHeight)
    context.drawImage(strokesCanvas, 0, 0)

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
}

export default function TransmissionSketcher({
    question,
    initialValue,
    onAnswer,
    patientSex,
    readOnly,
}: TransmissionSketcherProps) {
    const classes = useStyles()
    const currentHcs = useCurrentHcs()
    const { transmissionId } = useParams()
    const { mutateAsync: uploadPJ } = usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ()
    const { mutateAsync: deletePJ } = useDeleteIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ()
    const stageRef = useRef<Konva.Stage | null>(null)
    const uploadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hasPendingUploadRef = useRef(false)
    const uploadedMediaIdRef = useRef<string | null>(null)
    const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())
    // picture drawn on caregiver, displayed as is until the user chooses to draw again
    const [remoteImageUrl, setRemoteImageUrl] = useState<string | undefined>(() => {
        const { strokesValue, imageUrl } = splitInitialValue(initialValue)
        return parseStrokes(strokesValue).length === 0 ? imageUrl : undefined
    })
    // the picture is protected, its fetched with the api and displayed through a blob url
    const [backgroundUrl, setBackgroundUrl] = useState<string | undefined>(undefined)
    const [strokes, setStrokes] = useState<KonvaStroke[]>([])
    const [undoStack, setUndoStack] = useState<KonvaStroke[][]>([])
    const [redoStack, setRedoStack] = useState<KonvaStroke[][]>([])
    const [draftValue, setDraftValue] = useState<string>(() => {
        const { strokesValue, imageUrl } = splitInitialValue(initialValue)
        return strokesValue || imageUrl || ''
    })
    const toolRef = useRef<KonvaToolType>('brush')
    const isDrawing = useRef(false)
    const currentStrokeIdRef = useRef<string | null>(null)
    const strokesRef = useRef<KonvaStroke[]>([])

    useEffect(() => {
        const { strokesValue, imageUrl } = splitInitialValue(initialValue)
        const restoredStrokes = parseStrokes(strokesValue)
        setRemoteImageUrl(restoredStrokes.length === 0 ? imageUrl : undefined)
        setDraftValue(strokesValue || imageUrl || '')
        strokesRef.current = restoredStrokes
        setStrokes(restoredStrokes)
        setUndoStack([])
        setRedoStack([])
    }, [question.id])

    useEffect(() => {
        setBackgroundUrl(undefined)
        if (!remoteImageUrl) return
        let cancelled = false
        let objectUrl: string | undefined

        const loadRemoteImage = async () => {
            try {
                const mediaId = decodeURIComponent(remoteImageUrl.split(/[?#]/)[0].split('/').pop() ?? '')
                const blob = await getIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJMediaId(
                    currentHcs.id,
                    transmissionId ?? '',
                    mediaId,
                )
                if (cancelled) return
                objectUrl = URL.createObjectURL(blob)
                setBackgroundUrl(objectUrl)
            } catch (e) {
                console.error(`[loadRemoteImage]: ${e}`)
            }
        }
        loadRemoteImage()

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [remoteImageUrl, currentHcs.id, transmissionId])

    const isAnswered = useMemo(() => {
        if (question.required) return draftValue.trim().length > 0
        return draftValue.trim().length > 0
    }, [draftValue, question.required])

    // a picture drawn on caregiver and the recap cannot be edited
    const isLocked = Boolean(readOnly) || Boolean(remoteImageUrl)

    const canUndo = strokes.length > 0
    const canRedo = redoStack.length > 0
    const canResetAll = strokes.length > 0

    const bodyUrl = String(patientSex).toLowerCase() === 'femme' ? BodyPartFemaleUrl : BodyPartMaleUrl

    const removeUploadedSketch = async () => {
        const mediaId = uploadedMediaIdRef.current
        if (!mediaId) return
        uploadedMediaIdRef.current = null
        try {
            await deletePJ({ hcsId: currentHcs.id, transmissionId: transmissionId ?? '', params: { mediaId } })
        } catch (e) {
            console.error(`[removeUploadedSketch]: ${e}`)
        }
    }

    // keeps a picture of the drawing on the transmission so caregiver can display it
    const uploadSketchPicture = () => {
        hasPendingUploadRef.current = false
        const stage = stageRef.current
        if (!stage || !transmissionId || !question.id) return
        const hasStrokes = strokesRef.current.length > 0
        const capture = hasStrokes ? captureSketch(stage) : null

        // one upload at a time so the previous picture id is always known before it is replaced
        const previousUpload = uploadQueueRef.current
        const runUpload = async () => {
            await previousUpload
            try {
                await removeUploadedSketch()
                if (!capture) return
                const blob = await exportSketchBlob(capture, bodyUrl)
                if (!blob) return
                const uploaded = await uploadPJ({
                    hcsId: currentHcs.id,
                    transmissionId,
                    data: {
                        File: new File([blob], 'plaies.jpg', { type: 'image/jpeg' }),
                        TransmissionStepId: question.id?.replace('W.Q.', 'W.A.'),
                        Name: 'Photo des plaies',
                    },
                })
                uploadedMediaIdRef.current = uploaded.documentId ?? null
            } catch (e) {
                console.error(`[uploadSketchPicture]: ${e}`)
            }
        }
        uploadQueueRef.current = runUpload()
    }

    const scheduleSketchUpload = () => {
        hasPendingUploadRef.current = true
        if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current)
        uploadTimerRef.current = setTimeout(uploadSketchPicture, UPLOAD_DEBOUNCE_MS)
    }

    // leaving the question must not lose the last strokes
    useLayoutEffect(
        () => () => {
            if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current)
            if (hasPendingUploadRef.current) uploadSketchPicture()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const syncOnAnswer = (nextStrokes: KonvaStroke[]) => {
        const next = nextStrokes.length > 0 ? JSON.stringify(nextStrokes) : ''
        setDraftValue(next)
        onAnswer?.(next)
        scheduleSketchUpload()
    }

    const beginStroke = (point: { x: number; y: number }) => {
        const id = makeId()
        const tool = toolRef.current
        const stroke: KonvaStroke = { id, tool, points: [point.x, point.y] }

        setUndoStack((prev) => [...prev, strokes])
        setRedoStack([])
        strokesRef.current = [...strokesRef.current, stroke]
        setStrokes(strokesRef.current)
        currentStrokeIdRef.current = id
        isDrawing.current = true
    }

    const addPointToStroke = (point: { x: number; y: number }) => {
        const id = currentStrokeIdRef.current
        if (!id) return

        setStrokes((prev) => {
            const idx = prev.findIndex((s) => s.id === id)
            if (idx === -1) return prev
            const next = prev.slice()
            const stroke = next[idx]
            next[idx] = { ...stroke, points: stroke.points.concat([point.x, point.y]) }
            strokesRef.current = next
            return next
        })
    }

    const endStroke = () => {
        isDrawing.current = false
        currentStrokeIdRef.current = null
    }

    const handleUndo = () => {
        if (!canUndo) return
        setUndoStack((prevUndo) => {
            if (prevUndo.length === 0) return prevUndo
            const current = strokes
            setRedoStack((prevRedo) => [...prevRedo, current])

            const prev = prevUndo[prevUndo.length - 1]
            strokesRef.current = prev
            setStrokes(prev)
            syncOnAnswer(prev)
            return prevUndo.slice(0, -1)
        })
    }

    const handleRedo = () => {
        if (!canRedo) return
        setRedoStack((prevRedo) => {
            if (prevRedo.length === 0) return prevRedo

            const next = prevRedo[prevRedo.length - 1]
            setRedoStack(prevRedo.slice(0, -1))
            setUndoStack((prevUndo) => [...prevUndo, strokes])
            strokesRef.current = next
            setStrokes(next)
            syncOnAnswer(next)
            return prevRedo
        })
    }

    const handleResetAll = () => {
        strokesRef.current = []
        setStrokes([])
        setUndoStack([])
        setRedoStack([])
        syncOnAnswer([])
    }

    const sketchAreaRef = useRef<HTMLDivElement | null>(null)
    const [stageSize, setStageSize] = useState({ width: 800, height: 283 })

    useEffect(() => {
        const el = sketchAreaRef.current
        if (!el) return

        const updateSize = () => {
            setStageSize({ width: el.clientWidth, height: 283 })
        }
        updateSize()
        const observer = new ResizeObserver(updateSize)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handlePointerDown = (point: { x: number; y: number }) => {
        if (readOnly) return
        beginStroke(point)
    }

    const handlePointerMove = (point: { x: number; y: number }) => {
        if (readOnly) return
        if (!isDrawing.current) return
        addPointToStroke(point)
    }

    const handlePointerUp = () => {
        if (readOnly) return
        if (!isDrawing.current) return
        endStroke()
        syncOnAnswer(strokesRef.current)
    }

    return (
        <Box className={classes.container}>
            <Box sx={sketcherSx.sketchCard}>
                <>
                    <Box sx={sketcherSx.toolbarRow}>
                        <Box sx={sketcherSx.buttonGroup}>
                            <Button
                                variant="outlined"
                                disabled={isLocked || !canUndo}
                                onClick={handleUndo}
                                className={classes.actionButton}
                                startIcon={<UndoIcon />}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={isLocked || !canRedo}
                                onClick={handleRedo}
                                className={classes.actionButton}
                                endIcon={<RedoIcon />}
                            >
                                Rétablir
                            </Button>
                        </Box>
                        <Divider variant="middle" orientation="vertical" flexItem sx={sketcherSx.verticalDivider} />
                        <Button
                            variant="text"
                            onClick={handleResetAll}
                            disabled={isLocked || !canResetAll}
                            sx={sketcherSx.resetButton}
                            startIcon={<CloseIcon />}
                        >
                            Tout réinitialiser
                        </Button>
                    </Box>
                    <Divider sx={sketcherSx.horizontalDivider} />
                </>
                <Box sx={sketcherSx.sketchLockWrapper}>
                    <TransmissionSketcherKonva
                        containerRef={sketchAreaRef}
                        strokes={strokes}
                        stageSize={stageSize}
                        patientSex={patientSex}
                        stageRef={stageRef}
                        backgroundUrl={backgroundUrl}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        readOnly={isLocked}
                    />
                    {isLocked && (
                        <CommonTooltip
                            title={
                                <Box display="flex" alignItems="center" width="fit-content">
                                    <LockIcon sx={{ marginRight: '0.5rem' }} />
                                    <Typography>Saisie non modifiable</Typography>
                                </Box>
                            }
                            componentsProps={{ tooltip: { sx: { minWidth: 0 } } }}
                        >
                            <Box aria-disabled="true" sx={sketcherSx.sketchLockOverlay} />
                        </CommonTooltip>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
